import type {FieldPolicy} from "@apollo/client"
import {isMatching, match, P} from "ts-pattern"

type Keys = Array<string | Keys>

/**
 * Options for controlling pagination cache keys
 *
 * @see https://www.apollographql.com/docs/react/pagination/core-api
 */
interface PaginateOffsetArgs {
  /**
   * List of keys to omit from the cacheKey, defaults to `['offset']` and this is likely all you need.
   * @see https://www.apollographql.com/docs/react/caching/cache-field-behavior
   */
  keyArgsOmit?: string[]
  /**
   * List of keys to include in the cacheKey, you likely want `keyArgsOmit` otherwise new keys added to querys may unintentionally create separate cache objects
   * @see https://www.apollographql.com/docs/react/caching/cache-field-behavior
   */
  keysArgs?: Keys
}

const defaultKeysArgs: Keys = ["offset"]

/**
 * Paginate Offset for use with apollo client `typePolicies`
 *
 * This provides a wrapper around `keyArgs` and `merge` in a style which aligns with our offset pagination approach (i.e schema using `list.schema('Entity', EntityList'))`
 *
 * @see https://www.apollographql.com/docs/react/pagination/core-api/#offset-based-pagination
 * @throws when schema for pagination is used incorrectly, this is intended to highlight bad configuration sooner than silently broken pagination
 */
export function paginateOffset(paginateOptions: PaginateOffsetArgs = {}): FieldPolicy<unknown> {
  return {
    keyArgs: (args, context) => {
      if (!args) {
        console.error('error in paginateOffset for"', context)
        throw new Error("expected paginateOffset to be used with arguments")
      }
      const {keyArgsOmit = defaultKeysArgs, keysArgs} = paginateOptions

      return match({keyArgsOmit, keysArgs})
        .with({keysArgs: P.select(P.nonNullable)}, (keep) => keep)
        .with({keyArgsOmit: P.select(P.nonNullable)}, (omit) => Object.keys(args).filter((key) => !omit?.includes(key)))
        .exhaustive()
    },

    read(existing, {args}) {
      if (isMatching({results: P.array(P._)}, existing)) {
        const offset = args?.offset ?? 0
        const limit = args?.limit ?? existing.results.length
        const end = offset + limit

        // Extract the slice for this page
        const slice = existing.results.slice(offset, end)

        // Check if we have all the data for this range (no null/undefined gaps)
        const hasCompleteData = slice.length === limit && slice.every((item) => item !== null && item !== undefined)

        if (hasCompleteData) {
          return existing
        }
      }
      // Return undefined to trigger a fetch for missing data
      return undefined
    },

    merge(existing, incoming, options) {
      const {args} = options
      if (!args) {
        console.error("error in paginateOffset for", options)
        throw new Error("expected paginateOffset to be used with arguments")
      }

      return match([existing, incoming])
        .with([undefined, {results: P.array(), offset: P.number, hasMore: P.boolean}], ([_, data]) => data)
        .with(
          [{results: P.array()}, {results: P.array(), offset: P.number, hasMore: P.boolean}],
          ([{results: existingResults}, latest]) => {
            const offset = args.offset ?? 0
            const merged = existingResults.slice(0)

            // Fill gaps between existing data and new offset with null
            // This prevents sparse arrays which Apollo v3 doesn't handle correctly
            // an alternative is to make paginated reads just return their specific page, but this breaks infinite scroll views
            for (let i = existingResults.length; i < offset; i++) {
              merged[i] = null
            }

            // Insert new results at the offset position
            for (let i = 0; i < latest.results.length; ++i) {
              merged[offset + i] = latest.results[i]
            }

            return {
              ...(existing || {}),
              ...latest,
              results: merged
            }
          }
        )
        .otherwise((data) => {
          console.error(
            "error in paginateOffset. Pagination requests must be of the form {results: [], offset: number, hasMore: boolean}",
            ...data
          )
          throw new Error("Pagination Developer Error")
        })
    }
  }
}
